import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Alert,
  Paper,
  MenuItem,
  TextField,
} from '@mui/material'
import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { request } from '../common/services/request'
import { formatLocalDateTime } from '../common/utils'
import { API_ENDPOINTS } from '../common/endpoints'
import { getPatientDetailRoute } from '../common/constant'
import type { GenerationJob, JobListResponse, JobStatus } from '../common/types'
import { JOB_STATUSES } from '../common/types'

function Jobs() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [jobs, setJobs] = useState<GenerationJob[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string>('')

  const fetchJobs = async () => {
    try {
      setLoading(true)
      const client = request()
      const params: any = { page: page + 1, page_size: pageSize }
      if (statusFilter) params.status = statusFilter

      const res = await client.get<JobListResponse>(API_ENDPOINTS.JOBS.BASE, { params })
      setJobs(res.data.items)
      setTotal(res.data.total)
    } catch (e: any) {
      setError(e.message || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [page, pageSize, statusFilter])

  const handleCancel = async (id: number) => {
    try {
      const client = request()
      await client.post(API_ENDPOINTS.JOBS.CANCEL(id))
      setSuccess('Job cancelled')
      fetchJobs()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to cancel job')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this job?')) return

    try {
      const client = request()
      await client.delete(API_ENDPOINTS.JOBS.BY_ID(id))
      setSuccess('Job deleted')
      fetchJobs()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete job')
    }
  }

  const getStatusColor = (
    status: JobStatus
  ): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success'
      case 'failed':
        return 'error'
      case 'running':
        return 'info'
      case 'pending':
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Generation Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage generation jobs
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box mb={2}>
        <TextField
          select
          label="Filter by Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          {JOB_STATUSES.map((status) => (
            <MenuItem key={status.value} value={status.value}>
              {status.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Generated</TableCell>
                  <TableCell>Pushed</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {(jobs?.length ?? 0) === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="textSecondary">No jobs found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => (
                    <TableRow key={job.id} hover sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ cursor: 'pointer', color: 'primary.main' }}
                          onClick={() => navigate(getPatientDetailRoute(job.patient_id))}
                        >
                          #{job.patient_id}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={job.profile} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          size="small"
                          color={getStatusColor(job.status)}
                        />
                      </TableCell>
                      <TableCell>{job.vitals_generated}</TableCell>
                      <TableCell>{job.vitals_pushed}</TableCell>
                      <TableCell>
                        {job.vitals_failed > 0 ? (
                          <Chip
                            label={job.vitals_failed}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ) : (
                          job.vitals_failed
                        )}
                      </TableCell>
                      <TableCell>
                        {formatLocalDateTime(job.created_at)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(getPatientDetailRoute(job.patient_id))}
                          title="View Patient"
                        >
                          <ViewIcon />
                        </IconButton>
                        {(job.status === 'pending' || job.status === 'running') && (
                          <IconButton
                            size="small"
                            onClick={() => handleCancel(job.id)}
                            title="Cancel"
                            color="warning"
                          >
                            <CancelIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(job.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </Box>
  )
}

export default Jobs
